'use client';

import { css, cx } from '@/styled-system/css';
import ListingSearch from '@/src/components/ListingSearch';
import type { AreaResponse } from '@/src/lib/api';
import { useIsDesktop } from '@/src/lib/hooks/ui/useIsDesktop';
import { searchWrapper } from '../../_shell/searchListingSearch.styles';
import { usePathname } from '@/src/i18n/navigation';
import { useSrpViewMode } from './ViewModeContext';

type Props = {
  currentAreaFallback?: AreaResponse | null;
  variant?: 'sticky' | 'floating' | 'header';
};

const stickyWrapper = css({
  background: 'bg.neutral',
  paddingTop: 'tight',
  marginTop: '-tighter',
  paddingBottom: 'tight',
  paddingInline: 'tighter',
  position: 'sticky',
  top: 0,
  zIndex: 'zIndexStep',
  lg: {
    display: 'none',
  },
});

const floatingWrapper = css({
  width: '100%',
  background: 'transparent',
  position: 'static',
});

const headerWrapper = css({
  display: { base: 'none', lg: 'block' },
  width: '100%',
  maxWidth: 'breakpoint-lg',
  minWidth: 0,
});

const headerSearchWrapper = css({
  width: '100%',
  borderRadius: 'pill',
  backgroundColor: 'bg.action.neutral.alt.default',
  // HeaderNav owns the vertical rhythm. Use an outline so the desktop SRP
  // search surface keeps the framed treatment without increasing header height.
  outlineStyle: 'solid',
  outlineColor: 'border.action.neutral.default',
  outlineWidth: { base: 'default', _dark: 'zero' },
});

const feedModeStickyWrapper = css({
  '&[data-search-bar-variant="sticky"][data-srp-feed-mode="true"]': {
    position: 'static',
    lg: {
      display: 'none',
    },
  },
});

function isHdpPath(pathname: string) {
  const finalSegment = pathname.split('/').filter(Boolean).at(-1);
  return finalSegment === 'pad' || finalSegment === 'building';
}

export function SrpListingSearchBar({ currentAreaFallback, variant = 'sticky' }: Props) {
  const pathname = usePathname();
  const { viewMode } = useSrpViewMode();
  const isDesktop = useIsDesktop();
  const isFeedMode = viewMode === 'feed' && !isDesktop;
  const wrapperClass = variant === 'floating' ? floatingWrapper : variant === 'header' ? headerWrapper : stickyWrapper;
  const searchSurfaceClass = variant === 'header' ? headerSearchWrapper : searchWrapper;

  if (variant === 'header' && isHdpPath(pathname)) {
    return null;
  }

  return (
    <div
      className={cx(wrapperClass, feedModeStickyWrapper)}
      data-search-bar-variant={variant}
      data-srp-feed-mode={isFeedMode ? 'true' : 'false'}
      data-srp-component="search-bar"
    >
      <div className={searchSurfaceClass} data-srp-component="search-bar-surface">
        <ListingSearch size="sm" nearMeArea={currentAreaFallback} />
      </div>
    </div>
  );
}
